import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LOGO_SRC } from "@/constants/site";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "am_admin_token";

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function errorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return err?.message || "Something went wrong.";
}

function LoginCard({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Welcome back!");
      onSuccess();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid place-items-center px-4">
      <div className="w-full max-w-md relative">
        <div className="absolute inset-x-0 -top-14 flex justify-center">
          <img src={LOGO_SRC} alt="AM Group" className="h-24 w-auto object-contain drop-shadow-xl" />
        </div>
        <form
          onSubmit={submit}
          data-testid="admin-login-form"
          className="mt-6 rounded-3xl bg-white border border-brand-line shadow-[0_40px_100px_-40px_rgba(78,52,46,.5)] p-8 pt-16"
        >
          <div className="text-center">
            <div className="kicker mx-auto">Admin Portal</div>
            <h2 className="display-serif mt-2 text-3xl font-bold text-brand-dark">Sign in</h2>
            <p className="text-sm text-brand-mute mt-1">Manage all student enquiries from one place.</p>
          </div>
          <div className="mt-6 space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-brand-primary font-semibold">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email-input"
                className="mt-1 w-full rounded-lg border border-brand-line bg-brand-cream px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-gold"
                placeholder="you@amgroup.in"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-brand-primary font-semibold">Password</label>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-password-input"
                  className="mt-1 w-full rounded-lg border border-brand-line bg-brand-cream px-4 py-3 pr-11 focus:outline-none focus:ring-2 focus:ring-brand-gold"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShow((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded grid place-items-center text-brand-mute hover:text-brand-primary"
                  aria-label="Toggle password"
                >
                  <i className={`fa-solid ${show ? "fa-eye-slash" : "fa-eye"}`} />
                </button>
              </div>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="btn-primary mt-6 w-full justify-center"
          >
            {loading ? <><i className="fa-solid fa-spinner fa-spin" /> Signing in…</> : <>Sign In <i className="fa-solid fa-arrow-right" /></>}
          </button>
          <p className="mt-4 text-xs text-brand-mute text-center">Only the site owner can access this area.</p>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const { data } = await axios.get(`${API}/admin/contacts`, { headers: { Authorization: `Bearer ${token}` } });
      setContacts(data);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
        return;
      }
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      await axios.delete(`${API}/admin/contacts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setContacts((c) => c.filter((x) => x.id !== id));
      setActive(null);
      toast.success("Enquiry deleted.");
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const filtered = contacts.filter((c) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return [c.name, c.email, c.phone, c.subject, c.course, c.message].some((v) => (v || "").toString().toLowerCase().includes(q));
  });

  return (
    <div className="min-h-[80vh] py-14 px-4 sm:px-6 lg:px-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <span className="kicker">Admin Console</span>
            <h1 className="display-serif mt-2 text-3xl sm:text-4xl font-bold text-brand-dark">All Enquiries</h1>
            <p className="text-sm text-brand-mute mt-1">{contacts.length} total · sorted by newest first</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-brand-mute" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, email, phone…"
                data-testid="admin-search"
                className="w-64 max-w-full pl-9 pr-3 py-2.5 rounded-full border border-brand-line bg-white focus:outline-none focus:ring-2 focus:ring-brand-gold text-sm"
              />
            </div>
            <button onClick={load} className="btn-outline !px-4 !py-2 text-xs" data-testid="admin-refresh">
              <i className="fa-solid fa-rotate" /> Refresh
            </button>
            <button onClick={onLogout} className="btn-primary !px-4 !py-2 text-xs" data-testid="admin-logout">
              <i className="fa-solid fa-right-from-bracket" /> Logout
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Total Enquiries", value: contacts.length, icon: "inbox" },
            { label: "Today", value: contacts.filter((c) => (new Date(c.created_at)).toDateString() === (new Date()).toDateString()).length, icon: "clock" },
            { label: "This Week", value: contacts.filter((c) => (Date.now() - new Date(c.created_at).getTime()) < 7*86400000).length, icon: "calendar-week" },
            { label: "Unique Emails", value: new Set(contacts.map((c) => c.email)).size, icon: "envelope" },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl bg-white border border-brand-line p-4">
              <i className={`fa-solid fa-${s.icon} text-brand-gold`} />
              <div className="display-serif text-2xl font-extrabold text-brand-dark mt-1">{s.value}</div>
              <div className="text-[10px] uppercase tracking-widest text-brand-mute">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Table */}
        <div className="mt-8 rounded-2xl bg-white border border-brand-line overflow-hidden">
          {loading ? (
            <div className="p-16 text-center text-brand-mute">
              <i className="fa-solid fa-spinner fa-spin text-2xl text-brand-primary" />
              <div className="mt-3">Loading enquiries…</div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center text-brand-mute">
              <i className="fa-solid fa-inbox text-4xl text-brand-line" />
              <div className="mt-3">No enquiries match your search.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-brand-cream text-brand-dark">
                  <tr>
                    <th className="text-left px-5 py-3 font-semibold">Date</th>
                    <th className="text-left px-5 py-3 font-semibold">Name</th>
                    <th className="text-left px-5 py-3 font-semibold">Email</th>
                    <th className="text-left px-5 py-3 font-semibold">Phone</th>
                    <th className="text-left px-5 py-3 font-semibold">Subject / Course</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => (
                    <tr key={c.id} className="border-t border-brand-line hover:bg-brand-cream/40 transition-colors" data-testid={`admin-row-${c.id}`}>
                      <td className="px-5 py-4 text-brand-mute whitespace-nowrap">{fmtDate(c.created_at)}</td>
                      <td className="px-5 py-4 font-semibold text-brand-dark">{c.name}</td>
                      <td className="px-5 py-4"><a href={`mailto:${c.email}`} className="text-brand-primary hover:text-brand-gold">{c.email}</a></td>
                      <td className="px-5 py-4"><a href={`tel:${c.phone}`} className="text-brand-primary hover:text-brand-gold">{c.phone}</a></td>
                      <td className="px-5 py-4">
                        <div className="text-brand-dark">{c.subject || <span className="text-brand-mute italic">—</span>}</div>
                        {c.course && <div className="text-xs text-brand-mute mt-0.5">{c.course}</div>}
                      </td>
                      <td className="px-5 py-4 text-right whitespace-nowrap">
                        <button onClick={() => setActive(c)} className="text-brand-primary hover:text-brand-gold font-semibold text-xs" data-testid={`admin-view-${c.id}`}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail modal */}
      {active && (
        <div className="fixed inset-0 z-[70] bg-brand-dark/70 backdrop-blur-sm grid place-items-center p-4" onClick={() => setActive(null)}>
          <div
            className="w-full max-w-2xl bg-white rounded-2xl border border-brand-line shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-detail-modal"
          >
            <div className="bg-brand-dark text-white p-5 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-brand-gold">Enquiry Detail</div>
                <div className="display-serif text-xl font-bold mt-1">{active.name}</div>
              </div>
              <button onClick={() => setActive(null)} className="w-10 h-10 rounded-full hover:bg-white/10" aria-label="Close">
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <dl className="grid grid-cols-2 gap-y-3 gap-x-4">
                <dt className="text-brand-primary uppercase text-[10px] tracking-widest font-semibold">Email</dt>
                <dd className="text-brand-dark break-all"><a href={`mailto:${active.email}`} className="hover:text-brand-gold">{active.email}</a></dd>
                <dt className="text-brand-primary uppercase text-[10px] tracking-widest font-semibold">Phone</dt>
                <dd className="text-brand-dark"><a href={`tel:${active.phone}`} className="hover:text-brand-gold">{active.phone}</a></dd>
                <dt className="text-brand-primary uppercase text-[10px] tracking-widest font-semibold">Subject</dt>
                <dd className="text-brand-dark">{active.subject || "—"}</dd>
                <dt className="text-brand-primary uppercase text-[10px] tracking-widest font-semibold">Course</dt>
                <dd className="text-brand-dark">{active.course || "—"}</dd>
                <dt className="text-brand-primary uppercase text-[10px] tracking-widest font-semibold">Received</dt>
                <dd className="text-brand-dark">{fmtDate(active.created_at)}</dd>
              </dl>
              <div>
                <div className="text-brand-primary uppercase text-[10px] tracking-widest font-semibold mb-1">Message</div>
                <div className="rounded-xl bg-brand-cream border border-brand-line p-4 whitespace-pre-wrap text-brand-ink">{active.message}</div>
              </div>
              <div className="flex justify-between items-center pt-2">
                <a href={`https://wa.me/${(active.phone || "").replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="btn-outline !px-4 !py-2 text-xs">
                  <i className="fa-brands fa-whatsapp" /> WhatsApp
                </a>
                <button onClick={() => remove(active.id)} className="text-red-600 hover:text-red-700 text-sm font-semibold" data-testid="admin-delete">
                  <i className="fa-solid fa-trash mr-2" /> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setChecking(false); return; }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setAuthed(true))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  if (checking) {
    return (
      <div className="min-h-[70vh] grid place-items-center">
        <div className="loader-cap" />
      </div>
    );
  }
  return authed ? (
    <>
      <SEO title="Admin Dashboard — AM Group of Education" description="Admin console." noindex />
      <Dashboard onLogout={logout} />
    </>
  ) : (
    <>
      <SEO title="Admin Login — AM Group of Education" description="Sign in to the AM Group admin console." noindex />
      <LoginCard onSuccess={() => setAuthed(true)} />
    </>
  );
}
